import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface MyBookEntry {
    id: number;
    userId: number;
    profileId: number;
    notes: string;
    savedAt: string;
    createdAt: string;
    updatedAt: string;
}

interface MyBookState {
    items: MyBookEntry[];
    loading: boolean;
    error: string | null;
    total: number;
    currentPage: number;
    pageSize: number;
}

const initialState: MyBookState = {
    items: [],
    loading: false,
    error: null,
    total: 0,
    currentPage: 0,
    pageSize: 10,
};

const myBookSlice = createSlice({
    name: 'myBook',
    initialState,
    reducers: {
        // Fetch My Book
        fetchMyBookStart: (state) => {
            state.loading = true;
            state.error = null;
        },
        fetchMyBookSuccess: (
            state,
            action: PayloadAction<{
                items: MyBookEntry[];
                total: number;
                page: number;
                size: number;
            }>
        ) => {
            state.items = action.payload.items;
            state.total = action.payload.total;
            state.currentPage = action.payload.page;
            state.pageSize = action.payload.size;
            state.loading = false;
        },
        fetchMyBookError: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
            state.loading = false;
        },

        // Add to My Book
        addToMyBookStart: (state) => {
            state.loading = true;
            state.error = null;
        },
        addToMyBookSuccess: (state, action: PayloadAction<MyBookEntry>) => {
            const exists = state.items.find((item) => item.profileId === action.payload.profileId);
            if (!exists) {
                state.items.unshift(action.payload);
                state.total += 1;
            }
            state.loading = false;
        },
        addToMyBookError: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
            state.loading = false;
        },

        // Remove from My Book
        removeFromMyBookStart: (state) => {
            state.loading = true;
            state.error = null;
        },
        removeFromMyBookSuccess: (state, action: PayloadAction<number>) => {
            state.items = state.items.filter((item) => item.profileId !== action.payload);
            state.total = Math.max(0, state.total - 1);
            state.loading = false;
        },
        removeFromMyBookError: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
            state.loading = false;
        },

        // Update notes
        updateNotesStart: (state) => {
            state.loading = true;
            state.error = null;
        },
        updateNotesSuccess: (
            state,
            action: PayloadAction<{ profileId: number; notes: string }>
        ) => {
            const item = state.items.find(
                (item) => item.profileId === action.payload.profileId
            );
            if (item) {
                item.notes = action.payload.notes;
            }
            state.loading = false;
        },
        updateNotesError: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
            state.loading = false;
        },

        // Clear
        clearMyBook: (state) => {
            state.items = [];
            state.total = 0;
            state.currentPage = 0;
            state.error = null;
        },
    },
});

export const {
    fetchMyBookStart,
    fetchMyBookSuccess,
    fetchMyBookError,
    addToMyBookStart,
    addToMyBookSuccess,
    addToMyBookError,
    removeFromMyBookStart,
    removeFromMyBookSuccess,
    removeFromMyBookError,
    updateNotesStart,
    updateNotesSuccess,
    updateNotesError,
    clearMyBook,
} = myBookSlice.actions;

export default myBookSlice.reducer;
